import { defaultRegion, supportedRegions } from '@cash-web/support/utils-region-config'

const FALLBACK_LOCALE = supportedRegions[defaultRegion]?.defaultLocale || 'en-US'

/**
 * Get the best locale for a given region.
 * Priority: 1. URL locale → 2. Browser locale → 3. Region default
 */
export function getBestLocaleForRegion(region: string | undefined, urlLocale?: string): string {
  const normalizedRegion = region?.toLowerCase() || defaultRegion
  const regionConfig =
    supportedRegions[normalizedRegion as keyof typeof supportedRegions] || supportedRegions[defaultRegion]
  const supportedLocales = regionConfig?.supportedLocales || []
  const regionDefaultLocale = regionConfig?.defaultLocale || FALLBACK_LOCALE

  // 1. Check URL locale
  if (urlLocale && urlLocale !== 'default' && isSupportedLocale(urlLocale, supportedLocales)) {
    return urlLocale
  }

  // 2. Check browser locale
  if (typeof navigator !== 'undefined') {
    const browserLocales = navigator.languages || [navigator.language]
    const browserMatch = findSupportedLocale(browserLocales, supportedLocales)
    if (browserMatch) return browserMatch
  }

  // 3. Return default
  return regionDefaultLocale
}

function findExactMatch(locale: string, supportedLocales: readonly string[]): string | undefined {
  return supportedLocales.find(supported => supported.toLowerCase() === locale.toLowerCase())
}

export function isSupportedLocale(locale: string, supportedLocales: readonly string[]): boolean {
  return !!findExactMatch(locale, supportedLocales)
}

/** Find prefix match (e.g., "es" matches "es-US") */
function findPrefixMatch(candidate: string, supportedLocales: readonly string[]): string | undefined {
  const langPrefix = candidate.toLowerCase().split('-')[0]
  return supportedLocales.find(locale => locale.toLowerCase().startsWith(langPrefix + '-'))
}

/** Find the first supported locale from a list of candidates (case-insensitive, with prefix matching) */
export function findSupportedLocale(candidates: readonly string[], supportedLocales: readonly string[]): string | null {
  let match: string | undefined
  candidates.find(candidate => {
    match = findExactMatch(candidate, supportedLocales) ?? findPrefixMatch(candidate, supportedLocales)
    return match
  })
  return match ?? null
}
